import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import PrintableQR from "@/components/PrintableQR";
import PinAdjuster from "@/components/PinAdjuster";
import { MapPin, Clock, QrCode, Save, Loader2, ImagePlus, X, Link as LinkIcon, Check, Move, RotateCw, Zap } from "lucide-react";

const DAYS = [
  { key: "mon", label: "Monday" }, { key: "tue", label: "Tuesday" }, { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" }, { key: "fri", label: "Friday" }, { key: "sat", label: "Saturday" }, { key: "sun", label: "Sunday" },
];

/**
 * Shared editor for a user's baker map presence.
 * Reads/writes via GET/PATCH /api/me/location.
 * Props:
 *   mode: 'seller' — no is_baker toggle, no business_name (uses seller_profile.business_name), used inside Seller Dashboard
 *         'independent' — shows "I want to be on the baker map" toggle + business_name input
 *   username: current user's username (for QR modal)
 *   sellerBusinessName?: string (for QR modal when mode=seller)
 *   sellerAccent?: string (theme accent color for QR border)
 */
const isSellerMode = (mode) => mode === "seller";
export default function BakerLocationSection({ mode = "independent", username, sellerBusinessName, sellerAccent, adminTargetUserId }) {
  const [loc, setLoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const dirty = useRef(false);

  // API paths swap to admin variants when editing on behalf of another user
  const apiPath = adminTargetUserId ? `/admin/users/${adminTargetUserId}/location` : "/me/location";

  // Deep link that the QR code encodes — opens the Baker Finder map focused on this baker's pin.
  const publicUrl = username
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/find-a-baker?shop=${encodeURIComponent(username)}`
    : "";

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — try selecting the link manually");
    }
  };

  useEffect(() => {
    api.get(apiPath).then((r) => {
      // Admin endpoint returns { user, baker_location, seller_profile } — unwrap
      const raw = r.data || {};
      setLoc(adminTargetUserId ? (raw.baker_location || {}) : (raw || {}));
    }).catch(() => setLoc({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTargetUserId]);

  const set = (patch) => { dirty.current = true; setLoc({ ...(loc || {}), ...patch }); };
  const setDay = (dayKey, patch) => {
    const h = (loc?.hours || {})[dayKey] || {};
    set({ hours: { ...(loc?.hours || {}), [dayKey]: { ...h, ...patch } } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(apiPath, loc || {});
      setLoc(data);
      dirty.current = false;
      toast.success(adminTargetUserId ? "Saved on behalf of user" : "Location saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  // Fine-tune the pin by dragging — records the new coords and marks the location as manually-positioned
  // so subsequent address edits don't wipe the manual placement.
  const onPinDrag = ({ latitude, longitude }) => {
    dirty.current = true;
    setLoc({ ...(loc || {}), latitude, longitude, manually_positioned: true });
    toast.success("Pin moved — remember to Save");
  };

  // Reset manual placement — clear override and re-geocode from address next save.
  const resetPin = async () => {
    if (!window.confirm("Reset the pin to the auto-geocoded location? Any manual adjustment will be lost.")) return;
    dirty.current = true;
    const nextLoc = { ...(loc || {}), manually_positioned: false };
    setLoc(nextLoc);
    setSaving(true);
    try {
      // Send both flag + a tiny address touch by re-sending street to trigger re-geocode
      const { data } = await api.patch(apiPath, { ...nextLoc, manually_positioned: false });
      setLoc(data);
      dirty.current = false;
      toast.success("Pin reset to the geocoded location");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (loc === null) {
    return <div className="cp-card p-6 grid place-items-center text-[#857163]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const isSeller = mode === "seller";

  return (
    <div className="cp-card p-5 sm:p-6" data-testid="baker-location-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <div className="text-sm font-medium text-[#3B2C24] inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4" style={{ color: "#2F80ED" }} /> Baker Map presence
          </div>
          <p className="text-xs text-[#857163] mt-0.5 max-w-lg">
            {isSeller
              ? "Your shop pin on the Find-a-Baker map. Separate from your shop profile — toggle visibility any time."
              : "Add yourself to the Find-a-Baker map — perfect for home bakers, pop-ups, and stands without a full marketplace shop."}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <div className="inline-flex items-center gap-1 rounded-full border border-[#EBDCD1] bg-white pl-3 pr-1 py-1 max-w-[260px]" data-testid="finder-url-pill">
            <LinkIcon className="w-3.5 h-3.5 shrink-0 text-[#857163]" />
            <span className="text-xs text-[#3B2C24] truncate" title={publicUrl}>{publicUrl.replace(/^https?:\/\//, "")}</span>
            <button
              type="button"
              onClick={copyUrl}
              className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${copied ? "bg-[#8EAC50] text-white" : "bg-[#FF7E67] text-white hover:opacity-90"}`}
              data-testid="finder-url-copy-btn"
            >
              {copied ? <><Check className="w-3 h-3" /> Copied</> : "Copy"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowQR(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] transition"
            data-testid="qr-open-btn"
          >
            <QrCode className="w-3.5 h-3.5" style={{ color: "#8E7CC3" }} /> Print QR code
          </button>
        </div>
      </div>

      {!isSeller && (
        <label className="flex items-center gap-2 cursor-pointer bg-[#F9EFE6] rounded-2xl px-3 py-2 mb-3" data-testid="is-baker-toggle">
          <span className="text-sm text-[#3B2C24] flex-1">I want to appear on the Find-a-Baker map</span>
          <Switch checked={!!loc.is_baker} onCheckedChange={(v) => set({ is_baker: v, public: v ? loc.public : false })} data-testid="is-baker" />
        </label>
      )}

      {/* Stand Type — controls which pin (cake pop vs. red barn) shows on the Locator map */}
      {(isSeller || loc.is_baker) && (
        <div className="mb-3">
          <div className="text-xs text-[#857163] mb-1.5">Stand Type <span className="text-[#B79885]">(controls the pin shown on the map)</span></div>
          <div className="grid grid-cols-2 gap-2" data-testid="stand-type-toggle">
            {[
              { key: "cake_pops", label: "Cake Pop Stand", accent: "#FF7E67" },
              { key: "farm_stand", label: "Farm Stand", accent: "#B91C1C" },
            ].map((v) => {
              const active = (loc.vendor_type || "cake_pops") === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => set({ vendor_type: v.key })}
                  className="px-3 py-2 rounded-2xl text-sm font-semibold border-2 transition"
                  style={active
                    ? { background: v.accent, color: "#fff", borderColor: v.accent }
                    : { background: "#fff", color: "#3B2C24", borderColor: "#E7DED5" }}
                  data-testid={`stand-type-${v.key}`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 cursor-pointer" data-testid="loc-public-toggle">
        <span className="text-sm text-[#3B2C24] flex-1">Show my pin publicly</span>
        <Switch
          checked={!!loc.public}
          onCheckedChange={(v) => set({ public: v })}
          disabled={!isSeller && !loc.is_baker}
          data-testid="loc-public"
        />
      </label>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {!isSeller && (
          <label className="block sm:col-span-2">
            <span className="text-xs text-[#857163]">Display name (shown on the pin)</span>
            <input className="cp-input mt-1" value={loc.business_name || ""} onChange={(e) => set({ business_name: e.target.value })} placeholder={`${username}'s Kitchen`} maxLength={80} data-testid="loc-business-name" />
          </label>
        )}
        <label className="block sm:col-span-2">
          <span className="text-xs text-[#857163]">Street address</span>
          <input className="cp-input mt-1" value={loc.street || ""} onChange={(e) => set({ street: e.target.value })} placeholder="123 Main St" maxLength={160} data-testid="loc-street" />
        </label>
        <label className="block">
          <span className="text-xs text-[#857163]">City</span>
          <input className="cp-input mt-1" value={loc.city || ""} onChange={(e) => set({ city: e.target.value })} placeholder="Boston" maxLength={80} data-testid="loc-city" />
        </label>
        <label className="block">
          <span className="text-xs text-[#857163]">State / Region</span>
          <input className="cp-input mt-1" value={loc.state || ""} onChange={(e) => set({ state: e.target.value })} placeholder="MA" maxLength={80} data-testid="loc-state" />
        </label>
        <label className="block">
          <span className="text-xs text-[#857163]">ZIP / Postal code</span>
          <input className="cp-input mt-1" value={loc.postal_code || ""} onChange={(e) => set({ postal_code: e.target.value })} placeholder="02101" maxLength={20} data-testid="loc-postal-code" />
        </label>
        <label className="block">
          <span className="text-xs text-[#857163]">Country</span>
          <input className="cp-input mt-1" value={loc.country || ""} onChange={(e) => set({ country: e.target.value })} placeholder="USA" maxLength={80} data-testid="loc-country" />
        </label>
      </div>

      {/* Pin fine-tune: drag the marker to the exact spot on the map */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Move className="w-4 h-4 text-[#FF7E67]" />
            <span className="text-sm font-semibold text-[#3B2C24]">Fine-tune your pin</span>
            {loc.manually_positioned && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#8EAC50]/15 text-[#5E7A2E] font-semibold">Manual</span>
            )}
          </div>
          {loc.manually_positioned && (
            <button
              type="button"
              onClick={resetPin}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs text-[#857163] hover:text-[#3B2C24] disabled:opacity-50"
              data-testid="reset-pin-btn"
            >
              <RotateCw className="w-3.5 h-3.5" /> Reset to auto
            </button>
          )}
        </div>
        <p className="text-xs text-[#857163] mb-2">
          If the pin isn't quite right, <b>drag it</b> to the exact spot buyers should look for. This overrides the auto-geocoded location.
        </p>
        <PinAdjuster latitude={loc.latitude} longitude={loc.longitude} onChange={onPinDrag} />
      </div>

      <div className="mt-4">
        <div className="text-xs text-[#857163] mb-1.5">
          Finder logo <span className="text-[#B79885]">(shown on your map popup — <b>800 × 300 px landscape</b> works best; portrait/square photos will be cropped to fit)</span>
        </div>
        {loc.finder_logo_url ? (
          <div className="relative rounded-2xl overflow-hidden border border-[#EBDCD1]" style={{ background: sellerAccent || "#FF7E67" }}>
            <img src={fileUrl(loc.finder_logo_url)} alt="Finder logo" className="w-full h-32 object-cover" />
            <button
              type="button"
              onClick={() => set({ finder_logo_url: null })}
              className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 shadow hover:bg-white"
              aria-label="Remove finder logo"
              data-testid="finder-logo-remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="cp-card p-6 flex items-center justify-center gap-2 cursor-pointer hover:bg-[#F9EFE6]/50 transition" data-testid="finder-logo-upload">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  set({ finder_logo_url: data.path });
                  toast.success("Logo uploaded — click Save to keep it");
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
              }}
            />
            <ImagePlus className="w-5 h-5 text-[#FF7E67]" />
            <span className="text-sm text-[#857163]">Upload finder logo · <b>800 × 300 px</b> landscape</span>
          </label>
        )}
      </div>

      {/* Finder menu — up to 6 products shown on the map popup */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5 gap-3 flex-wrap">
          <div className="text-xs text-[#857163]">
            Menu <span className="text-[#B79885]">(up to 6 items shown on your map popup — <b>500 × 500 px square</b> works best)</span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="menu-enabled-toggle">
            <Switch
              checked={loc.menu_enabled !== false}
              onCheckedChange={(v) => set({ menu_enabled: v })}
              data-testid="menu-enabled-switch"
            />
            <span className="text-xs text-[#857163] whitespace-nowrap">{loc.menu_enabled !== false ? "Show menu" : "Hidden"}</span>
          </label>
        </div>
        {loc.menu_enabled === false ? (
          <div className="rounded-xl border border-dashed border-[#EBDCD1] p-6 text-center text-sm text-[#857163]" data-testid="menu-hidden-notice">
            Menu is hidden — your popup won&apos;t show a menu section. Toggle on to display up to 6 items.
          </div>
        ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="finder-menu-grid">
          {[0, 1, 2, 3, 4, 5].map((idx) => {
            const menu = loc.finder_menu || [];
            const item = menu[idx];
            const updateItem = (patch) => {
              const next = [...(loc.finder_menu || [])];
              while (next.length <= idx) next.push({ image_url: null, description: "" });
              next[idx] = { ...next[idx], ...patch };
              set({ finder_menu: next });
            };
            const removeItem = () => {
              const next = [...(loc.finder_menu || [])];
              next[idx] = { image_url: null, description: "" };
              // Trim trailing empties
              while (next.length && !next[next.length - 1].image_url && !next[next.length - 1].description) next.pop();
              set({ finder_menu: next });
            };
            return (
              <div key={idx} className="rounded-xl border border-[#EBDCD1] p-2 space-y-2 bg-white" data-testid={`finder-menu-slot-${idx}`}>
                {item?.image_url ? (
                  <div className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={fileUrl(item.image_url)} alt={item.description || `menu ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={removeItem}
                      className="absolute top-1 right-1 bg-white/95 rounded-full p-1 shadow hover:bg-white"
                      aria-label="Remove menu item"
                      data-testid={`finder-menu-remove-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-[#EBDCD1] hover:border-[#FF7E67] flex flex-col items-center justify-center cursor-pointer transition" data-testid={`finder-menu-upload-${idx}`}>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append("file", file);
                        try {
                          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          updateItem({ image_url: data.path });
                          toast.success(`Menu item ${idx + 1} added — click Save to keep it`);
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                    />
                    <ImagePlus className="w-4 h-4 text-[#FF7E67]" />
                    <span className="text-[10px] text-[#857163] mt-1">Item {idx + 1}</span>
                  </label>
                )}
                <input
                  type="text"
                  value={item?.description || ""}
                  onChange={(e) => updateItem({ description: e.target.value })}
                  placeholder="Description"
                  maxLength={80}
                  className="cp-input !text-xs !py-1.5"
                  data-testid={`finder-menu-desc-${idx}`}
                />
              </div>
            );
          })}
        </div>
        )}
      </div>

      <div className="mt-4">
        <label className="block">
          <span className="text-xs text-[#857163]">Public note (visible on the map)</span>
          <input className="cp-input mt-1" value={loc.note || ""} onChange={(e) => set({ note: e.target.value })} placeholder="Weekend pickups · DM to order" maxLength={200} data-testid="loc-note" />
          <div className="text-[10px] text-[#857163] text-right mt-0.5">{(loc.note || "").length}/200</div>
        </label>
      </div>

      {/* Farm-stand-only: rotating "currently carrying" text */}
      {loc.vendor_type === "farm_stand" && (
        <div className="mt-4 p-3 rounded-2xl border-2 border-dashed" style={{ borderColor: "#B91C1C33", background: "#FFF5F5" }}>
          <label className="block">
            <span className="text-xs font-semibold" style={{ color: "#B91C1C" }}>Currently carrying <span className="font-normal text-[#857163]">(rotate this whenever your stand's stock changes)</span></span>
            <input
              className="cp-input mt-1"
              value={loc.currently_carrying || ""}
              onChange={(e) => set({ currently_carrying: e.target.value })}
              placeholder="Heirloom tomatoes, fresh basil, sourdough loaves"
              maxLength={200}
              data-testid="loc-currently-carrying"
            />
            <div className="text-[10px] text-[#857163] text-right mt-0.5">{(loc.currently_carrying || "").length}/200</div>
          </label>
        </div>
      )}

      {/* Storefront on/off toggle — controls the "Shop" button on the map pin (sellers only) */}
      {isSeller && (
        <label className="mt-4 flex items-center gap-2 cursor-pointer bg-[#F1F7E5] rounded-2xl px-3 py-2" data-testid="storefront-enabled-toggle">
          <div className="flex-1">
            <div className="text-sm text-[#3B2C24] font-semibold">Enable my Storefront</div>
            <div className="text-[11px] text-[#5E7A2E]">Shows a "Shop" button on your map pin that opens your storefront.</div>
          </div>
          <Switch checked={!!loc.storefront_enabled} onCheckedChange={(v) => set({ storefront_enabled: v })} data-testid="storefront-enabled" />
        </label>
      )}

      {/* Pop-up Mode — temporary map pin override with expiry */}
      <div className="mt-4 p-3 rounded-2xl border-2 border-dashed" style={{ borderColor: "#F4A83B55", background: "#FFF9EF" }} data-testid="popup-mode-card">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" style={{ color: "#F4A83B" }} />
            <span className="text-sm font-heading font-semibold text-[#3B2C24]">Pop-up Mode</span>
          </div>
          <Switch
            checked={!!loc.popup_enabled}
            onCheckedChange={(v) => set({ popup_enabled: v })}
            data-testid="popup-enable-toggle"
          />
        </div>
        <p className="text-xs text-[#857163] mb-2 leading-snug">
          Temporarily move your map pin to a market or event. Your home pin returns automatically when the pop-up expires.
        </p>
        {loc.popup_enabled && (
          <div className="space-y-2.5" data-testid="popup-mode-fields">
            <label className="block">
              <span className="text-xs text-[#857163]">Pop-up address <span className="text-[#B79885]">(street, city, state)</span></span>
              <input
                className="cp-input mt-1"
                value={loc.popup_address || ""}
                onChange={(e) => set({ popup_address: e.target.value })}
                placeholder="Union Square, New York, NY"
                maxLength={240}
                data-testid="popup-address"
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#857163]">Short note <span className="text-[#B79885]">(shown on the pin)</span></span>
              <input
                className="cp-input mt-1"
                value={loc.popup_note || ""}
                onChange={(e) => set({ popup_note: e.target.value })}
                placeholder="Union Sq Farmers Market · Sat 8am–2pm"
                maxLength={120}
                data-testid="popup-note"
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#857163]">Expires</span>
              <input
                type="datetime-local"
                className="cp-input mt-1"
                value={loc.popup_expires_at ? new Date(loc.popup_expires_at).toISOString().slice(0, 16) : ""}
                onChange={(e) => set({ popup_expires_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                data-testid="popup-expires-at"
              />
            </label>
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "Rest of today", hours: () => { const d = new Date(); d.setHours(23, 59, 0, 0); return d; } },
                { label: "This weekend", hours: () => { const d = new Date(); const day = d.getDay(); const daysToSun = (7 - day) % 7 || 7; d.setDate(d.getDate() + daysToSun); d.setHours(23, 59, 0, 0); return d; } },
                { label: "+3 hours", hours: () => new Date(Date.now() + 3 * 60 * 60 * 1000) },
              ].map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => set({ popup_expires_at: preset.hours().toISOString() })}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-[#FFD07F]/40 border border-[#F4A83B]/40 text-[#B26A00] font-semibold hover:bg-[#FFD07F]/70 transition"
                  data-testid={`popup-preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {loc.popup_expires_at && (
              <div className="text-[11px] text-[#B26A00] pt-1">
                Pop-up ends <b>{new Date(loc.popup_expires_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</b>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="text-xs text-[#857163] mb-1.5">Dietary options offered <span className="text-[#B79885]">(optional — helps buyers filter)</span></div>
        <div className="flex flex-wrap gap-1.5" data-testid="dietary-tags">
          {["keto", "vegan", "gluten-free", "nut-free", "dairy-free"].map((tag) => {
            const active = (loc.dietary_tags || []).includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  const cur = loc.dietary_tags || [];
                  set({ dietary_tags: active ? cur.filter((t) => t !== tag) : [...cur, tag] });
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  active
                    ? "bg-[#FF7E67] text-white border-[#FF7E67]"
                    : "bg-white text-[#3B2C24] border-[#E7DED5] hover:border-[#FF7E67]"
                }`}
                data-testid={`dietary-tag-${tag}`}
              >
                {tag === "keto" ? "Keto Friendly" : tag === "vegan" ? "Vegan Options" : tag === "gluten-free" ? "Gluten-free" : tag === "nut-free" ? "Nut-free" : "Dairy-free"}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-sm font-medium text-[#3B2C24] mb-2 flex items-center gap-1.5"><Clock className="w-4 h-4" style={{ color: "#2F80ED" }} /> Hours of operation</div>
        <p className="text-[11px] text-[#857163] mb-2">The map automatically shows &ldquo;Open until…&rdquo; during your hours and flips to &ldquo;Closed&rdquo; after your closing time — no need to toggle anything manually.</p>
        <div className="rounded-2xl border border-[#EBDCD1] overflow-hidden">
          {DAYS.map((day) => {
            const h = (loc.hours || {})[day.key] || {};
            const closed = !!h.closed;
            return (
              <div key={day.key} className="grid grid-cols-[110px_auto_1fr_1fr] gap-2 items-center px-3 py-2 border-b border-[#EBDCD1]/60 last:border-b-0 bg-white">
                <div className="text-sm font-medium text-[#3B2C24]">{day.label}</div>
                <label className="flex items-center gap-1.5 text-xs text-[#857163] cursor-pointer">
                  <input type="checkbox" checked={closed} onChange={(e) => setDay(day.key, { closed: e.target.checked })} className="w-3.5 h-3.5" style={{ accentColor: "#857163" }} data-testid={`hrs-closed-${day.key}`} />
                  Closed
                </label>
                <input type="time" value={h.open || ""} onChange={(e) => setDay(day.key, { open: e.target.value })} disabled={closed} className="cp-input !py-1 !text-sm disabled:opacity-40" data-testid={`hrs-open-${day.key}`} />
                <input type="time" value={h.close || ""} onChange={(e) => setDay(day.key, { close: e.target.value })} disabled={closed} className="cp-input !py-1 !text-sm disabled:opacity-40" data-testid={`hrs-close-${day.key}`} />
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-[#857163] mt-1.5">Hours show in the map popup + your printable QR page.</div>
      </div>

      <div className="mt-5 flex justify-end">
        <button onClick={save} disabled={saving} className="cp-btn-primary inline-flex items-center gap-1.5 disabled:opacity-70" data-testid="save-location-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save location"}
        </button>
      </div>

      <PrintableQR
        open={showQR}
        onClose={() => setShowQR(false)}
        businessName={loc.business_name || sellerBusinessName || username}
        username={username}
        note={loc.note}
        hours={loc.hours}
        accent={sellerAccent || "#FF7E67"}
      />
    </div>
  );
}
